import { createContext, useCallback, useEffect, useState } from "react";
import apiService from "@/lib/api";

export const BrandingContext = createContext(null);

const FAVICON_ID = "brand-favicon";

// Color configuration is disabled — applying tenant colors as CSS vars was
// reskinning the live app (buttons/accents) in ways that weren't wanted.
// Kept here, commented out, in case colors are re-enabled later.
//
// const hexToRgb = (hex) => {
//   if (!hex) return null;
//   const clean = hex.replace(/^#/, "");
//   if (clean.length !== 6) return null;
//   const num = parseInt(clean, 16);
//   return `${(num >> 16) & 255} ${(num >> 8) & 255} ${num & 255}`;
// };
//
// const applyCssVars = (branding) => {
//   if (!branding) return;
//   const root = document.documentElement;
//
//   const primaryRgb = hexToRgb(branding.primaryColor);
//   const contrastRgb = hexToRgb(branding.primaryColorContrast);
//   const darkRgb = hexToRgb(branding.primaryColorDark);
//   const contrastDarkRgb = hexToRgb(branding.primaryColorContrastDark);
//
//   const vars = {
//     "--brand-primary": branding.primaryColor,
//     "--brand-primary-contrast": branding.primaryColorContrast,
//     "--brand-primary-dark": branding.primaryColorDark,
//     "--brand-primary-contrast-dark": branding.primaryColorContrastDark,
//     "--color-brand-primary": primaryRgb,
//     "--color-btn-primary": contrastRgb,
//     "--color-accent-primary": darkRgb,
//     "--color-accent-secondary": contrastDarkRgb,
//   };
//   Object.entries(vars).forEach(([key, value]) => {
//     if (value) root.style.setProperty(key, value);
//     else root.style.removeProperty(key);
//   });
// };

const applyFavicon = (url) => {
  if (!url) return;
  let link = document.getElementById(FAVICON_ID);
  if (!link) {
    link = document.createElement("link");
    link.id = FAVICON_ID;
    link.rel = "icon";
    document.head.appendChild(link);
  }
  link.href = url;
};

export const BrandingProvider = ({ children }) => {
  const [branding, setBranding] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchBranding = useCallback(async () => {
    try {
      const response = await apiService.get("/api/v2/public/branding");
      const raw = response?.data;
      const data = raw?.data ?? raw;

      // Always apply the fetched data — this only runs on mount and on the
      // explicit "branding-published" event, never on a poll, so there's no
      // flicker risk to guard against. A version-equality guard used to live
      // here to skip "unchanged" fetches, but a live-asset change (logo
      // upload/removal on the published record) doesn't bump `version`, so
      // it silently kept showing the removed asset until a hard reload.
      if (data && data.version > 0) {
        setBranding(data);
        // applyCssVars(data); // color configuration disabled
        applyFavicon(data.faviconUrl);
      } else {
        setBranding(null);
      }
    } catch {
      setBranding(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBranding();
  }, [fetchBranding]);

  useEffect(() => {
    const handleBrandingEvent = () => fetchBranding();
    window.addEventListener("branding-published", handleBrandingEvent);
    return () => window.removeEventListener("branding-published", handleBrandingEvent);
  }, [fetchBranding]);

  // Re-applied CSS vars on theme toggle (light/dark) — disabled along with
  // the rest of the color configuration feature.
  // useEffect(() => {
  //   const observer = new MutationObserver(() => {
  //     if (branding) applyCssVars(branding);
  //   });
  //   observer.observe(document.documentElement, {
  //     attributes: true,
  //     attributeFilter: ["data-theme"],
  //   });
  //   return () => observer.disconnect();
  // }, [branding]);

  return (
    <BrandingContext.Provider value={{ branding, loading, refresh: fetchBranding }}>
      {children}
    </BrandingContext.Provider>
  );
};
